import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Minus, X, ShoppingCart, Banknote, QrCode, CheckCircle2, Printer, Trash2, RefreshCw } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const receiptNumber = (value) => Math.round(Number(value || 0)).toLocaleString("id-ID");

const parsePromoDate = (value, endOfDay = false) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? `${raw}T${endOfDay ? "23:59:59.999" : "00:00:00"}`
    : raw;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isPromoCurrentlyActive = (promo, now = new Date()) => {
  if (!promo || promo.active === false) return false;
  const startsAt = parsePromoDate(promo.start_date, false);
  const endsAt = parsePromoDate(promo.end_date, true);
  if (startsAt && now < startsAt) return false;
  if (endsAt && now > endsAt) return false;
  return true;
};

const parseQrisAdditionalInfo = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const getQrisReceiptDetails = (sale) => {
  const qris = sale?.qris || {};
  const paidInfo = parseQrisAdditionalInfo(qris.paid_inquiry?.additionalInfo);
  const lastInfo = parseQrisAdditionalInfo(qris.last_inquiry?.additionalInfo);
  return {
    customerName: String(sale?.customer_name || paidInfo.customerName || lastInfo.customerName || "").trim(),
    approvalCode: String(qris.approval_code || paidInfo.approvalCode || lastInfo.approvalCode || "").trim(),
  };
};

export default function POS() {
  const { hasFeature } = useAuth();
  const canUsePromos = hasFeature("promos");
  const canUseTax = hasFeature("tax_ppn");
  const canUseQris = hasFeature("qris");

  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [promos, setPromos] = useState([]);
  const [promosLoading, setPromosLoading] = useState(true);
  const [promoChecking, setPromoChecking] = useState(false);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxRate, setTaxRate] = useState("11");
  const [method, setMethod] = useState("cash");
  const [cash, setCash] = useState("");
  const [paying, setPaying] = useState(false);
  const [qrisSale, setQrisSale] = useState(null);
  const [qrisMode, setQrisMode] = useState({ configured: false, provider: null, mode: null, label: "QRIS belum dikonfigurasi" });
  const [qrisModeLoading, setQrisModeLoading] = useState(false);
  const [staticQrImageUrl, setStaticQrImageUrl] = useState("");
  const [staticQrImageLoading, setStaticQrImageLoading] = useState(false);
  const [paidSale, setPaidSale] = useState(null);
  // Yokke inquiry polling state
  const [inquiryAttempts, setInquiryAttempts] = useState(0);
  const [inquiryStatus, setInquiryStatus] = useState(""); // "polling" | "exhausted" | "checking" | ""
  const [manualChecking, setManualChecking] = useState(false);
  const receiptRef = useRef(null);
  const pollingTimerRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [o, c] = await Promise.all([api.get("/outlets"), api.get("/categories")]);
      setOutlets(o.data || []);
      setCategories(c.data || []);
      if (o.data?.length) setOutletId(o.data[0].id);
    })();

    if (canUsePromos) {
      api.get("/promos")
        .then((r) => setPromos(r.data || []))
        .catch(() => {
          setPromos([]);
          toast.error("Daftar promo tidak dapat dimuat");
        })
        .finally(() => setPromosLoading(false));
    } else {
      setPromos([]);
      setPromosLoading(false);
    }
  }, [canUsePromos]);

  useEffect(() => {
    if (!canUsePromos) {
      setPromoCode("");
      setAppliedPromo(null);
    }
    if (!canUseTax) setTaxEnabled(false);
    if (!canUseQris && method === "qris") setMethod("cash");
  }, [canUsePromos, canUseTax, canUseQris, method]);

  const loadProducts = useCallback(async () => {
    if (!outletId) return;
    const response = await api.get("/products", { params: { outlet_id: outletId } });
    setProducts(response.data || []);
  }, [outletId]);

  useEffect(() => {
    if (!outletId) return undefined;
    setPromoCode("");
    setAppliedPromo(null);
    loadProducts().catch(() => toast.error("Data stok produk tidak dapat dimuat"));
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") loadProducts().catch(() => {});
    }, 5000);
    return () => window.clearInterval(timer);
  }, [outletId, loadProducts]);

  useEffect(() => {
    if (!canUseQris || !outletId) {
      setQrisMode({ configured: false, provider: null, mode: null, label: "QRIS tidak tersedia" });
      setMethod((current) => current === "qris" ? "cash" : current);
      return;
    }

    let active = true;
    setQrisModeLoading(true);
    api.get(`/qris-config/outlet/${outletId}/mode`)
      .then((response) => {
        if (!active) return;
        const next = response.data || { configured: false, provider: null, mode: null, label: "QRIS belum dikonfigurasi" };
        setQrisMode(next);
        if (!next.configured) setMethod((current) => current === "qris" ? "cash" : current);
      })
      .catch(() => {
        if (!active) return;
        setQrisMode({ configured: false, provider: null, mode: null, label: "QRIS belum dikonfigurasi" });
        setMethod((current) => current === "qris" ? "cash" : current);
      })
      .finally(() => { if (active) setQrisModeLoading(false); });

    return () => { active = false; };
  }, [outletId, canUseQris]);

  const activePromos = useMemo(() => {
    if (!canUsePromos || !outletId) return [];
    return promos
      .filter((promo) => {
        if (!isPromoCurrentlyActive(promo)) return false;
        const outletIds = Array.isArray(promo.outlet_ids) ? promo.outlet_ids : [];
        return outletIds.length === 0 || outletIds.includes(outletId);
      })
      .sort((a, b) => String(a.name || a.code).localeCompare(String(b.name || b.code), "id"));
  }, [promos, outletId, canUsePromos]);

  const filtered = useMemo(() => {
    let arr = products.filter((p) => p.active !== false);
    if (cat !== "all") arr = arr.filter((p) => p.category_id === cat);
    if (q) {
      const s = q.toLowerCase();
      arr = arr.filter((p) => p.name?.toLowerCase().includes(s) || p.sku?.toLowerCase().includes(s) || p.barcode?.toLowerCase().includes(s));
    }
    return arr;
  }, [products, cat, q]);

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const discount = appliedPromo?.discount || 0;
  const taxableBase = Math.max(0, subtotal - discount);
  const normalizedTaxRate = Math.min(100, Math.max(0, Number(taxRate || 0)));
  const tax = canUseTax && taxEnabled ? Math.round(taxableBase * normalizedTaxRate / 100) : 0;
  const total = Math.max(0, taxableBase + tax);
  const change = method === "cash" ? Math.max(0, Number(cash || 0) - total) : 0;

  const add = (p) => {
    setCart((c) => {
      const idx = c.findIndex((i) => i.product_id === p.id);
      if (idx >= 0) {
        const next = [...c]; next[idx] = { ...next[idx], qty: next[idx].qty + 1 }; return next;
      }
      return [...c, { product_id: p.id, name: p.name, qty: 1, price: p.price, cost: p.cost || 0 }];
    });
  };
  const inc = (i) => setCart((c) => c.map((x, idx) => idx === i ? { ...x, qty: x.qty + 1 } : x));
  const dec = (i) => setCart((c) => c.map((x, idx) => idx === i ? { ...x, qty: Math.max(1, x.qty - 1) } : x));
  const remove = (i) => setCart((c) => c.filter((_, idx) => idx !== i));
  const clear = () => {
    setCart([]);
    setPromoCode("");
    setAppliedPromo(null);
    setTaxEnabled(false);
    setCash("");
  };

  const applyPromo = async (options = {}) => {
    const { silent = false } = options;
    if (!canUsePromos) {
      setPromoCode("");
      setAppliedPromo(null);
      if (!silent) toast.error("Fitur promo tidak tersedia pada paket saat ini");
      return;
    }
    const selectedCode = String(promoCode || "").trim().toUpperCase();
    if (!selectedCode) {
      setAppliedPromo(null);
      return;
    }
    if (subtotal <= 0) {
      if (!silent) toast.error("Masukkan barang terlebih dahulu");
      setAppliedPromo(null);
      return;
    }

    setPromoChecking(true);
    try {
      const r = await api.post("/promos/validate", {
        code: selectedCode,
        subtotal,
        outlet_id: outletId,
      });
      setAppliedPromo(r.data);
      if (!silent) toast.success(`Promo ${r.data.promo.name} berhasil digunakan`);
    } catch (e) {
      setAppliedPromo(null);
      if (!silent) toast.error(e?.response?.data?.detail || "Promo tidak valid");
    } finally {
      setPromoChecking(false);
    }
  };

  useEffect(() => {
    if (!canUsePromos || !appliedPromo?.promo?.code || !promoCode) return;
    if (subtotal <= 0) {
      setAppliedPromo(null);
      return;
    }

    const timer = window.setTimeout(() => {
      applyPromo({ silent: true });
    }, 250);
    return () => window.clearTimeout(timer);
    // Recalculate the selected promo whenever the cart value or outlet changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, outletId, promoCode, canUsePromos]);

  const pay = async () => {
    if (cart.length === 0) { toast.error("Keranjang kosong"); return; }
    if (!outletId) { toast.error("Pilih outlet"); return; }
    if (method === "qris" && !canUseQris) { toast.error("Fitur QRIS tidak tersedia pada paket saat ini"); return; }
    if (method === "qris" && !qrisMode.configured) { toast.error("QRIS outlet belum dikonfigurasi"); return; }
    if (appliedPromo && !canUsePromos) { toast.error("Fitur promo tidak tersedia pada paket saat ini"); return; }
    if (taxEnabled && !canUseTax) { toast.error("Fitur PPN tidak tersedia pada paket saat ini"); return; }
    if (method === "cash" && Number(cash || 0) < total) { toast.error("Uang tunai kurang"); return; }
    setPaying(true);
    try {
      const items = cart.map((c) => ({ ...c, subtotal: c.qty * c.price }));
      const body = {
        outlet_id: outletId,
        items,
        discount: canUsePromos ? discount : 0,
        promo_code: canUsePromos ? (appliedPromo?.promo?.code || null) : null,
        tax: canUseTax ? tax : 0,
        payment_method: method,
        cash_received: method === "cash" ? Number(cash || 0) : 0,
      };
      const r = await api.post("/sales", body);
      if (method === "qris") {
        setQrisSale(r.data);
      } else {
        setPaidSale({ ...r.data, tax_rate: canUseTax && taxEnabled ? normalizedTaxRate : 0 });
        clear();
        await loadProducts();
        toast.success("Transaksi sukses");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memproses");
    } finally { setPaying(false); }
  };

  const doInquiry = useCallback(async (saleId, silent = false) => {
    try {
      const r = await api.post(`/sales/${saleId}/confirm-payment`);
      if (r.data?.status === "paid") {
        const sale = (await api.get(`/sales/${saleId}`)).data;
        setPaidSale(sale);
        setQrisSale(null);
        setInquiryStatus("");
        setInquiryAttempts(0);
        clear();
        await loadProducts();
        toast.success("Pembayaran QRIS sukses");
        return true;
      }
      if (!silent) toast.info("Belum terbayar. Coba lagi.");
      return false;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Gagal cek status");
      return false;
    }
  }, [loadProducts]);

  const manualInquiry = async () => {
    if (!qrisSale) return;
    setManualChecking(true);
    await doInquiry(qrisSale.id, false);
    setManualChecking(false);
  };

  useEffect(() => {
    if (!qrisSale || qrisSale.qris?.provider !== "static") {
      setStaticQrImageUrl("");
      setStaticQrImageLoading(false);
      return undefined;
    }

    let active = true;
    let objectUrl = "";
    const protectedPath = String(
      qrisSale.qris?.qr_url || `/qris-config/outlet/${qrisSale.outlet_id}/static-image`
    ).replace(/^\/api(?=\/|$)/, "");

    setStaticQrImageUrl("");
    setStaticQrImageLoading(true);
    api.get(protectedPath, { responseType: "blob" })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setStaticQrImageUrl(objectUrl);
      })
      .catch(() => {
        if (active) toast.error("Gambar QRIS statis tidak dapat dimuat");
      })
      .finally(() => { if (active) setStaticQrImageLoading(false); });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [qrisSale]);

  const confirmStaticPayment = async () => {
    if (!qrisSale || qrisSale.qris?.provider !== "static") return;
    const confirmed = window.confirm("Pastikan dana QRIS sudah terlihat pada rekening merchant. Konfirmasi pembayaran sekarang?");
    if (!confirmed) return;

    setManualChecking(true);
    try {
      const response = await api.post(`/sales/${qrisSale.id}/confirm-payment`, { manual_confirm: true });
      if (response.data?.status !== "paid") {
        toast.error(response.data?.message || "Pembayaran belum dapat dikonfirmasi");
        return;
      }
      const sale = (await api.get(`/sales/${qrisSale.id}`)).data;
      setPaidSale(sale);
      setQrisSale(null);
      clear();
      await loadProducts();
      toast.success("Pembayaran QRIS statis dikonfirmasi");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal mengonfirmasi pembayaran");
    } finally {
      setManualChecking(false);
    }
  };

  // Auto-poll only for Yokke dynamic QRIS, every 3s up to 5 times.
  useEffect(() => {
    if (!qrisSale || qrisSale.qris?.provider !== "yokke") {
      if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current);
      setInquiryAttempts(0); setInquiryStatus("");
      return;
    }
    if (inquiryAttempts >= 5) { setInquiryStatus("exhausted"); return; }
    setInquiryStatus("polling");
    pollingTimerRef.current = setTimeout(async () => {
      const ok = await doInquiry(qrisSale.id, true);
      if (!ok) setInquiryAttempts((n) => n + 1);
    }, 3000);
    return () => { if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current); };
  }, [qrisSale, inquiryAttempts, doInquiry]);

  const printReceipt = () => {
    if (!receiptRef.current) return;
    const win = window.open("", "_blank", "width=380,height=600");
    if (!win) {
      toast.error("Popup cetak diblokir browser");
      return;
    }
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Struk ${paidSale?.invoice_no || ""}</title><style>
      @page { size: 80mm auto; margin: 0; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; color: #000; }
      body { width: 80mm; font-family: "Courier New", Courier, monospace; font-size: 11px; line-height: 1.25; }
      .receipt-paper { width: 72mm; margin: 0 auto; padding: 4mm 0 8mm; }
      .receipt-store { text-align: center; font-size: 15px; line-height: 1.15; font-weight: 700; text-transform: uppercase; }
      .receipt-centre { text-align: center; }
      .receipt-small { font-size: 9px; }
      .receipt-separator { margin: 4px 0; overflow: hidden; white-space: nowrap; letter-spacing: .3px; }
      .receipt-meta, .receipt-row { display: flex; justify-content: space-between; gap: 8px; }
      .receipt-meta > :first-child, .receipt-row > :first-child { min-width: 0; }
      .receipt-meta > :last-child, .receipt-row > :last-child { flex: none; text-align: right; }
      .receipt-item { margin: 4px 0; }
      .receipt-item-name { font-weight: 700; text-transform: uppercase; overflow-wrap: anywhere; }
      .receipt-item-detail { display: grid; grid-template-columns: 1fr auto; gap: 8px; padding-left: 6px; }
      .receipt-total { font-size: 14px; font-weight: 700; margin-top: 2px; }
      .receipt-footer { text-align: center; margin-top: 7px; }
      .receipt-strong { font-weight: 700; }
    </style></head><body>${receiptRef.current.outerHTML}</body></html>`);
    win.document.close(); win.focus();
    setTimeout(() => { win.print(); win.close(); }, 200);
  };

  const receiptOutlet = paidSale ? outlets.find((o) => o.id === paidSale.outlet_id) : null;
  const receiptQty = paidSale?.items?.reduce((sum, item) => sum + Number(item.qty || 0), 0) || 0;
  const paidTaxableBase = paidSale ? Math.max(0, Number(paidSale.subtotal || 0) - Number(paidSale.discount || 0)) : 0;
  const paidTaxRate = paidSale?.tax_rate || (paidTaxableBase > 0 && Number(paidSale?.tax || 0) > 0
    ? Number(((Number(paidSale.tax) / paidTaxableBase) * 100).toFixed(2))
    : 0);
  const receiptPaymentLabel = paidSale?.payment_method === "qris"
    ? (paidSale?.qris?.provider === "static" ? "QRIS STATIS" : "QRIS DINAMIS")
    : "TUNAI";
  const qrisReceiptDetails = getQrisReceiptDetails(paidSale);

  return (
    <div className="h-[calc(100vh-56px)] grid grid-cols-1 lg:grid-cols-[1fr_400px]" data-testid="pos-page">
      {/* Left: products */}
      <div className="flex flex-col border-r border-border min-h-0">
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center gap-3">
            <select data-testid="pos-outlet-select" value={outletId} onChange={(e)=>setOutletId(e.target.value)} className="border border-border px-3 py-2 text-sm">
              {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <div className="flex-1 relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input data-testid="pos-search-input" value={q} onChange={(e)=>setQ(e.target.value)}
                className="w-full border border-border pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                placeholder="Cari nama / SKU / barcode…" />
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto">
            <button onClick={() => setCat("all")} className={"px-4 py-2 text-xs uppercase tracking-wider font-semibold border " + (cat==="all"?"bg-zinc-900 text-white border-zinc-900":"border-border")}>Semua</button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} className={"px-4 py-2 text-xs uppercase tracking-wider font-semibold border whitespace-nowrap " + (cat===c.id?"bg-zinc-900 text-white border-zinc-900":"border-border")}>{c.name}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-zinc-500 py-20">Belum ada produk. Tambahkan dari menu Produk.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 grid-borders">
              {filtered.map((p) => (
                <button key={p.id} data-testid={`pos-product-${p.id}`} onClick={() => add(p)}
                  className="p-4 text-left bg-white hover:bg-zinc-50 transition-colors btn-press">
                  <div className="aspect-square bg-zinc-100 mb-3 flex items-center justify-center overflow-hidden">
                    {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <span className="font-display font-black text-3xl text-zinc-300">{p.name?.[0]}</span>}
                  </div>
                  <div className="text-sm font-semibold line-clamp-2">{p.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 tabular-nums">Stok: {p.stock ?? 0}</div>
                  <div className="font-display font-bold text-base mt-2 tabular-nums">{formatIDR(p.price)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: cart */}
      <div className="flex flex-col bg-zinc-50 min-h-0">
        <div className="p-4 border-b border-border flex items-center justify-between bg-white">
          <div className="flex items-center gap-2"><ShoppingCart className="h-4 w-4"/><div className="overline">Keranjang ({cart.length})</div></div>
          {cart.length>0 && <button onClick={clear} data-testid="cart-clear-btn" className="text-xs text-rose-600 inline-flex items-center gap-1"><Trash2 className="h-3 w-3"/>Kosongkan</button>}
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {cart.length===0 && <div className="text-center text-sm text-zinc-500 py-20">Tap produk di kiri untuk menambah</div>}
          {cart.map((c, i) => (
            <div key={i} className="bg-white border border-border p-3" data-testid={`cart-item-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-semibold leading-tight">{c.name}</div>
                <button onClick={() => remove(i)} className="text-zinc-400 hover:text-rose-600"><X className="h-3.5 w-3.5"/></button>
              </div>
              <div className="text-xs text-zinc-500 mt-0.5 tabular-nums">{formatIDR(c.price)} / item</div>
              <div className="flex items-center justify-between mt-2">
                <div className="inline-flex items-center border border-border">
                  <button onClick={() => dec(i)} className="px-2 py-1.5 hover:bg-zinc-100"><Minus className="h-3 w-3"/></button>
                  <div className="px-3 text-sm font-semibold tabular-nums" data-testid={`cart-item-${i}-qty`}>{c.qty}</div>
                  <button onClick={() => inc(i)} className="px-2 py-1.5 hover:bg-zinc-100"><Plus className="h-3 w-3"/></button>
                </div>
                <div className="font-semibold tabular-nums text-sm">{formatIDR(c.qty * c.price)}</div>
              </div>
            </div>
          ))}
        </div>
        {/* Payment */}
        <div className="border-t border-border bg-white p-4 space-y-3">
          {canUsePromos && (
            <div className="space-y-1.5" data-testid="pos-promo-panel">
            <label htmlFor="pos-promo-select" className="overline text-zinc-500">Promo Aktif</label>
            <div className="flex gap-2">
              <select
                id="pos-promo-select"
                data-testid="pos-promo-select"
                value={promoCode}
                disabled={promosLoading || !outletId}
                onChange={(e) => {
                  setPromoCode(e.target.value);
                  setAppliedPromo(null);
                }}
                className="min-w-0 flex-1 border border-border px-3 py-2 text-sm bg-white disabled:bg-zinc-100 disabled:text-zinc-400"
              >
                <option value="">{promosLoading ? "Memuat promo..." : "Tanpa promo"}</option>
                {activePromos.map((promo) => {
                  const minimum = Number(promo.min_purchase || 0);
                  const notEligible = subtotal < minimum;
                  const benefit = promo.type === "percent"
                    ? `${Number(promo.value || 0)}%`
                    : formatIDR(promo.value || 0);
                  return (
                    <option key={promo.id || promo.code} value={promo.code} disabled={notEligible}>
                      {promo.code} — {promo.name} ({benefit})
                      {notEligible ? ` · min. ${formatIDR(minimum)}` : ""}
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                onClick={() => applyPromo()}
                disabled={!promoCode || promoChecking || subtotal <= 0}
                data-testid="pos-promo-apply-btn"
                className="px-4 py-2 text-xs font-semibold border border-zinc-900 hover:bg-zinc-900 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {promoChecking ? "CEK..." : "PAKAI"}
              </button>
            </div>
            {!promosLoading && activePromos.length === 0 && (
              <div className="text-xs text-zinc-500">Tidak ada promo aktif untuk outlet ini.</div>
            )}
            </div>
          )}
          {canUsePromos && appliedPromo && (
            <div className="flex items-center justify-between gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2">
              <span>
                {appliedPromo.promo.name} ({appliedPromo.promo.code}) · Potongan {formatIDR(appliedPromo.discount)}
              </span>
              <button
                type="button"
                onClick={() => { setPromoCode(""); setAppliedPromo(null); }}
                className="font-semibold text-emerald-800 hover:underline"
                data-testid="pos-promo-remove-btn"
              >
                Hapus
              </button>
            </div>
          )}
          {canUseTax && (
            <div className="border border-border bg-zinc-50 p-3" data-testid="pos-tax-panel">
            <div className="flex items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer">
                <input
                  type="checkbox"
                  checked={taxEnabled}
                  onChange={(e) => setTaxEnabled(e.target.checked)}
                  data-testid="pos-tax-toggle"
                  className="h-4 w-4 accent-[#002FA7]"
                />
                Tambahkan PPN
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  disabled={!taxEnabled}
                  data-testid="pos-tax-rate"
                  className="w-20 border border-border bg-white px-2 py-1.5 text-right text-sm tabular-nums disabled:bg-zinc-100 disabled:text-zinc-400"
                  aria-label="Tarif PPN"
                />
                <span className="text-sm text-zinc-600">%</span>
              </div>
            </div>
            {taxEnabled && (
              <div className="mt-2 flex justify-between text-xs text-zinc-500">
                <span>Dasar pengenaan setelah diskon</span>
                <span className="tabular-nums">{formatIDR(taxableBase)}</span>
              </div>
            )}
            </div>
          )}
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-zinc-600"><span>Subtotal</span><span className="tabular-nums">{formatIDR(subtotal)}</span></div>
            {discount>0 && <div className="flex justify-between text-zinc-600"><span>Diskon</span><span className="tabular-nums">- {formatIDR(discount)}</span></div>}
            {canUseTax && taxEnabled && tax>0 && <div className="flex justify-between text-zinc-600"><span>PPN ({normalizedTaxRate}%)</span><span className="tabular-nums">{formatIDR(tax)}</span></div>}
            <div className="flex justify-between font-display text-2xl font-black"><span>TOTAL</span><span className="tabular-nums" data-testid="pos-total">{formatIDR(total)}</span></div>
          </div>
          <div className={`grid ${canUseQris ? "grid-cols-2" : "grid-cols-1"} gap-0 border border-border`}>
            <button data-testid="pos-method-cash" onClick={() => setMethod("cash")} className={"py-3 text-xs font-semibold uppercase tracking-wider inline-flex items-center justify-center gap-1 " + (method==="cash"?"bg-zinc-900 text-white":"")}>
              <Banknote className="h-4 w-4"/>Tunai
            </button>
            {canUseQris && (
              <button
                data-testid="pos-method-qris"
                onClick={() => qrisMode.configured ? setMethod("qris") : toast.error("QRIS outlet belum dikonfigurasi")}
                disabled={qrisModeLoading || !qrisMode.configured}
                className={"py-3 text-xs font-semibold uppercase tracking-wider inline-flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed " + (method==="qris"?"bg-zinc-900 text-white":"")}
                title={qrisMode.configured ? qrisMode.label : "Atur QRIS melalui menu QRIS Outlet"}
              >
                <QrCode className="h-4 w-4"/>{qrisModeLoading ? "Memuat QRIS…" : qrisMode.label}
              </button>
            )}
          </div>
          {method==="cash" && (
            <div>
              <label className="overline text-zinc-600 block mb-1.5">Uang Diterima</label>
              <input data-testid="pos-cash-input" type="number" value={cash} onChange={(e)=>setCash(e.target.value)}
                className="w-full border border-border px-4 py-3 text-lg font-mono-num focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              {Number(cash)>=total && total>0 && <div className="mt-1 text-xs text-zinc-600">Kembalian: <span className="tabular-nums font-semibold" data-testid="pos-change">{formatIDR(change)}</span></div>}
              <div className="grid grid-cols-4 gap-1 mt-2">
                {[total, 50000, 100000, 200000].map((v, i) => (
                  <button key={i} onClick={()=>setCash(String(v))} className="text-xs py-2 border border-border hover:bg-zinc-100">{i===0?"Pas":formatIDR(v).replace("Rp ","")}</button>
                ))}
              </div>
            </div>
          )}
          <button onClick={pay} disabled={paying || cart.length===0} data-testid="pos-pay-btn"
            className="w-full h-14 bg-[#002FA7] text-white font-display text-lg font-bold tracking-tight hover:bg-[#00227A] disabled:opacity-50 btn-press transition-colors">
            {paying ? "Memproses…" : `BAYAR ${formatIDR(total)}`}
          </button>
        </div>
      </div>

      {/* QRIS dialog */}
      <Dialog open={!!qrisSale} onOpenChange={(o) => !o && setQrisSale(null)}>
        <DialogContent className="rounded-none border-border" data-testid="qris-dialog">
          <DialogHeader>
            <DialogTitle>{qrisSale?.qris?.provider === "static" ? "Bayar dengan QRIS Statis" : "Bayar dengan QRIS Dinamis"}</DialogTitle>
          </DialogHeader>
          {qrisSale && (
            <div className="space-y-4 text-center">
              <div className="overline text-zinc-500">{qrisSale.invoice_no} · {(qrisSale.qris?.provider||"QRIS").toUpperCase()}</div>
              <div className="font-display font-black text-4xl tabular-nums">{formatIDR(qrisSale.total)}</div>
              {qrisSale.qris?.provider === "static" ? (
                staticQrImageUrl ? (
                  <img src={staticQrImageUrl} alt="QRIS Statis" className="mx-auto w-64 h-64 object-contain bg-white border border-border p-2" />
                ) : (
                  <div className="mx-auto w-64 h-64 border border-border bg-zinc-50 flex items-center justify-center text-sm text-zinc-500">
                    {staticQrImageLoading ? "Memuat QRIS statis…" : "Gambar QRIS statis tidak tersedia"}
                  </div>
                )
              ) : qrisSale.qris?.qr_url ? (
                <img src={qrisSale.qris.qr_url} alt="QRIS" className="mx-auto w-64 h-64" />
              ) : (
                <div className="mx-auto inline-block p-4 bg-white border border-border">
                  <QRCodeSVG value={qrisSale.qris?.qr_string || qrisSale.id} size={240} />
                </div>
              )}
              {qrisSale.qris?.provider === "yokke" ? (
                <>
                  <p className="text-sm text-zinc-500">Pindai QR dengan aplikasi e-wallet/m-banking. Sistem otomatis melakukan inquiry setiap 3 detik, maksimal 5 kali.</p>
                  <div className="border border-border p-3 bg-zinc-50 text-left">
                    <div className="flex items-center justify-between">
                      <div className="overline text-zinc-500">Status Inquiry Yokke</div>
                      <div className="text-xs font-mono tabular-nums">{Math.min(inquiryAttempts, 5)}/5</div>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      {inquiryStatus === "polling" && <><RefreshCw className="h-3.5 w-3.5 animate-spin text-[#002FA7]"/><span>Mengecek pembayaran…</span></>}
                      {inquiryStatus === "exhausted" && <><span className="text-amber-700">5x inquiry otomatis selesai. Silakan cek manual.</span></>}
                      {inquiryStatus === "" && <span className="text-zinc-500">Menunggu pembayaran…</span>}
                    </div>
                    <div className="mt-2 flex gap-0.5">
                      {[0,1,2,3,4].map((i) => (
                        <div key={i} className={"h-1 flex-1 " + (i < inquiryAttempts ? "bg-[#002FA7]" : "bg-zinc-200")}/>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => { setQrisSale(null); toast.info("QRIS ditutup. Transaksi tetap PENDING dan dapat dipantau di Rekonsiliasi."); }} className="py-3 text-sm border border-border" data-testid="qris-cancel-btn">Tutup</button>
                    <button onClick={manualInquiry} disabled={manualChecking || (inquiryStatus !== "exhausted" && inquiryAttempts < 5)} data-testid="qris-manual-inquiry-btn"
                      className="py-3 text-sm bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-4 w-4"/>{manualChecking ? "Mengecek…" : "Inquiry Manual"}
                    </button>
                  </div>
                  {inquiryStatus !== "exhausted" && <p className="text-xs text-zinc-400">Tombol inquiry manual aktif setelah 5x inquiry otomatis.</p>}
                  <p className="text-xs text-amber-700">Menutup dialog tidak membatalkan QR di Yokke. Pembayaran terlambat dapat ditangani melalui Rekonsiliasi.</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-zinc-500">Pindai QRIS statis outlet. Sistem tidak melakukan inquiry otomatis.</p>
                  <div className="border border-amber-300 bg-amber-50 p-3 text-left text-xs text-amber-900">
                    Periksa mutasi atau notifikasi rekening merchant sebelum menekan konfirmasi. Konfirmasi akan mengubah transaksi internal menjadi PAID dan mengurangi stok.
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => { setQrisSale(null); toast.info("Transaksi QRIS statis tetap PENDING sampai dikonfirmasi kasir."); }} className="py-3 text-sm border border-border" data-testid="qris-cancel-btn">Tutup</button>
                    <button onClick={confirmStaticPayment} disabled={manualChecking} data-testid="qris-static-confirm-btn"
                      className="py-3 text-sm bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-4 w-4"/>{manualChecking ? "Memproses…" : "Konfirmasi Pembayaran"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt dialog */}
      <Dialog open={!!paidSale} onOpenChange={(o) => !o && setPaidSale(null)}>
        <DialogContent className="rounded-none border-border max-w-md" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle>Struk Pembayaran</DialogTitle>
          </DialogHeader>
          {paidSale && (
            <>
              <div ref={receiptRef} className="receipt-paper font-mono text-[11px] leading-tight bg-white text-black p-4 border border-zinc-200">
                <div className="receipt-store text-center font-bold text-base uppercase leading-tight">
                  {receiptOutlet?.name || "OUTLET"}
                </div>
                {receiptOutlet?.address && <div className="receipt-centre text-center mt-1">{receiptOutlet.address}</div>}
                {receiptOutlet?.phone && <div className="receipt-centre text-center">Telp. {receiptOutlet.phone}</div>}
                <div className="receipt-centre receipt-small text-center text-[10px] mt-1">BUKTI PEMBAYARAN</div>

                <div className="receipt-separator my-1 overflow-hidden whitespace-nowrap">========================================</div>
                <div className="receipt-meta flex justify-between gap-3"><span>No. Struk</span><span className="text-right">{paidSale.invoice_no}</span></div>
                <div className="receipt-meta flex justify-between gap-3"><span>Tanggal</span><span className="text-right">{new Date(paidSale.created_at).toLocaleDateString("id-ID")}</span></div>
                <div className="receipt-meta flex justify-between gap-3"><span>Jam</span><span className="text-right">{new Date(paidSale.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span></div>
                <div className="receipt-meta flex justify-between gap-3"><span>Kasir</span><span className="text-right uppercase">{paidSale.cashier_name || "-"}</span></div>
                <div className="receipt-meta flex justify-between gap-3"><span>Bayar</span><span className="text-right">{receiptPaymentLabel}</span></div>
                <div className="receipt-separator my-1 overflow-hidden whitespace-nowrap">----------------------------------------</div>

                {paidSale.items.map((it, i) => (
                  <div key={i} className="receipt-item my-1">
                    <div className="receipt-item-name font-bold uppercase break-words">{it.name}</div>
                    <div className="receipt-item-detail grid grid-cols-[1fr_auto] gap-3 pl-2">
                      <span>{it.qty} x {receiptNumber(it.price)}</span>
                      <span className="text-right">{receiptNumber(it.subtotal)}</span>
                    </div>
                  </div>
                ))}

                <div className="receipt-separator my-1 overflow-hidden whitespace-nowrap">----------------------------------------</div>
                <div className="receipt-row flex justify-between gap-3"><span>Subtotal</span><span>{receiptNumber(paidSale.subtotal)}</span></div>
                {Number(paidSale.discount || 0) > 0 && (
                  <div className="receipt-row flex justify-between gap-3"><span>Diskon / Hemat</span><span>-{receiptNumber(paidSale.discount)}</span></div>
                )}
                {Number(paidSale.tax || 0) > 0 && (
                  <div className="receipt-row flex justify-between gap-3">
                    <span>PPN{paidTaxRate ? ` (${paidTaxRate}%)` : ""}</span>
                    <span>{receiptNumber(paidSale.tax)}</span>
                  </div>
                )}
                <div className="receipt-row receipt-total flex justify-between gap-3 font-bold text-sm mt-1">
                  <span>TOTAL</span><span>Rp {receiptNumber(paidSale.total)}</span>
                </div>
                <div className="receipt-row flex justify-between gap-3"><span>{receiptPaymentLabel}</span><span>{receiptNumber(paidSale.cash_received || paidSale.total)}</span></div>
                {Number(paidSale.change || 0) > 0 && (
                  <div className="receipt-row flex justify-between gap-3"><span>Kembalian</span><span>{receiptNumber(paidSale.change)}</span></div>
                )}

                <div className="receipt-separator my-1 overflow-hidden whitespace-nowrap">----------------------------------------</div>
                <div className="receipt-row flex justify-between gap-3"><span>Jumlah item</span><span>{receiptQty}</span></div>
                {paidSale.payment_method === "qris" && (
                  <>
                    <div className="receipt-meta receipt-small flex justify-between gap-3 text-[10px]">
                      <span>Ref. QRIS</span>
                      <span className="text-right">{paidSale.qris?.reference_no || "-"}</span>
                    </div>
                    <div className="receipt-meta receipt-small flex justify-between gap-3 text-[10px]">
                      <span>Nama Customer</span>
                      <span className="text-right uppercase break-words">{qrisReceiptDetails.customerName || "-"}</span>
                    </div>
                    <div className="receipt-meta receipt-small flex justify-between gap-3 text-[10px]">
                      <span>Approval Code</span>
                      <span className="text-right">{qrisReceiptDetails.approvalCode || "-"}</span>
                    </div>
                  </>
                )}
                <div className="receipt-separator my-1 overflow-hidden whitespace-nowrap">========================================</div>
                <div className="receipt-footer text-center mt-2">
                  <div className="receipt-strong font-bold">TERIMA KASIH</div>
                  <div className="receipt-small text-[10px] mt-1">Simpan struk ini sebagai bukti pembayaran.</div>
                  <div className="receipt-small text-[10px]">Barang yang sudah dibeli mengikuti kebijakan retur outlet.</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button onClick={() => setPaidSale(null)} className="py-3 text-sm border border-border" data-testid="receipt-close-btn">Tutup</button>
                <button onClick={printReceipt} data-testid="receipt-print-btn" className="py-3 text-sm bg-zinc-900 text-white font-semibold inline-flex items-center justify-center gap-1"><Printer className="h-4 w-4"/>Cetak</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
